import { Divider, Group, Modal, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { SettlementTargetDto, isPersonTarget } from 'src/types/money-transfer.types';

import styles from './MoneyTransferDetailModal.module.scss';
import { Props } from './MoneyTransferDetailModal.types';

const MoneyTransferDetailModal = ({ open, onClose, transfer }: Props) => {
  const { t } = useTranslation();

  // A registered member can still have an empty name (invitation not completed yet).
  const getTargetName = (target: SettlementTargetDto) =>
    isPersonTarget(target)
      ? [target.firstName, target.lastName].filter(Boolean).join(' ') ||
        t('checklist.invitedUser', 'Invité')
      : target.name;

  const linkedExpenses = transfer?.expenses ?? [];

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('expenses.transferTitle', 'Remboursement')}
      size="md"
    >
      {transfer && (
        <div className={styles.container}>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {dayjs(transfer.date).format('DD/MM/YYYY')}
            </Text>
            <Text fw={600}>{`${transfer.amount.toFixed(2)} €`}</Text>
          </Group>

          <Group gap="xs">
            <Text size="sm">{getTargetName(transfer.from)}</Text>
            <IconArrowRight size={16} />
            <Text size="sm">{getTargetName(transfer.to)}</Text>
          </Group>

          <Divider />

          <Text fw={500}>{t('moneyTransfer.detail.linkedExpenses', 'Dépenses remboursées')}</Text>

          {linkedExpenses.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t(
                'moneyTransfer.detail.linkedExpensesEmpty',
                'Ce remboursement n’est rattaché à aucune dépense.'
              )}
            </Text>
          ) : (
            <div className={styles.expenses}>
              {linkedExpenses.map(expense => (
                <div key={expense.id} className={styles.expense}>
                  <div className={styles.expenseDetails}>
                    <Text size="sm" fw={500}>
                      {expense.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {dayjs(expense.paidAt).format('DD/MM/YYYY')}
                    </Text>
                  </div>
                  <Text size="sm">{`${expense.amount.toFixed(2)} €`}</Text>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default MoneyTransferDetailModal;
